<script setup lang="ts">
import { Form, Link } from "@inertiajs/vue3"
import { useTranslation } from "i18next-vue"
import PersistentLayout from "@/layouts/PersistentLayout.vue"
import Auth from "@/layouts/Auth.vue"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { sessions, signups } from "@/routes"

defineOptions({ layout: [PersistentLayout, Auth] })

const { t } = useTranslation()
const createRoute = signups.create()

const errs = (error: string | string[] | undefined) =>
  error ? [error].flat() : []
</script>

<template>
  <div class="flex flex-col gap-6">
    <Card>
      <CardHeader class="text-center">
        <CardTitle as="h1" class="text-xl" data-testid="page-title">
          {{ t("signup.title") }}
        </CardTitle>
        <CardDescription>
          {{ t("signup.description") }}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form
          :action="createRoute.url"
          :method="createRoute.method"
          #default="{ errors, processing }"
        >
          <FieldGroup>
            <Field>
              <FieldLabel for="code">
                {{ t("signup.code") }}
              </FieldLabel>
              <Input id="code" name="code" type="text" required />
            </Field>

            <Field>
              <FieldLabel for="first_name">
                {{ t("signup.first_name") }}
              </FieldLabel>
              <Input
                id="first_name"
                name="first_name"
                type="text"
                autocomplete="given-name"
                required
                :aria-invalid="Boolean(errors.firstName)"
              />
              <FieldError :errors="errs(errors.firstName)" />
            </Field>

            <Field>
              <FieldLabel for="last_name">
                {{ t("signup.last_name") }}
              </FieldLabel>
              <Input
                id="last_name"
                name="last_name"
                type="text"
                autocomplete="family-name"
                required
                :aria-invalid="Boolean(errors.lastName)"
              />
              <FieldError :errors="errs(errors.lastName)" />
            </Field>

            <Field>
              <FieldLabel for="email_address">
                {{ t("signup.email_address") }}
              </FieldLabel>
              <Input
                id="email_address"
                name="email_address"
                type="email"
                autocomplete="username"
                required
                :aria-invalid="Boolean(errors.emailAddress)"
              />
              <FieldError :errors="errs(errors.emailAddress)" />
            </Field>

            <Field>
              <FieldLabel for="password">
                {{ t("signup.password") }}
              </FieldLabel>
              <Input
                id="password"
                name="password"
                type="password"
                autocomplete="new-password"
                required
                :aria-invalid="Boolean(errors.password)"
              />
              <FieldError :errors="errs(errors.password)" />
            </Field>

            <Field>
              <FieldLabel for="password_confirmation">
                {{ t("signup.password_confirmation") }}
              </FieldLabel>
              <Input
                id="password_confirmation"
                name="password_confirmation"
                type="password"
                autocomplete="new-password"
                required
                :aria-invalid="Boolean(errors.passwordConfirmation)"
              />
              <FieldError :errors="errs(errors.passwordConfirmation)" />
            </Field>

            <Field>
              <Button type="submit" :disabled="processing">
                {{ t("signup.submit") }}
              </Button>
              <FieldDescription class="text-center">
                <Link :href="sessions.new().url">
                  {{ t("signup.back_to_login") }}
                </Link>
              </FieldDescription>
            </Field>
          </FieldGroup>
        </Form>
      </CardContent>
    </Card>
  </div>
</template>
